// @givmo/app-admin — Company Fundraisers LIST (EP-WEB3).
//
// The management landing for a company's OWN fundraisers. Reads `GET /admin/company/fundraisers`
// (a bare `List[FundraiserOut]`, all statuses) and renders a row per fundraiser (cover thumbnail · name ·
// status badge · goal · raised + donation count). FCM-WEB18: the badge is honest — Draft, Active, Closed
// (Active + `closed_at`), Deactivated, and "Taken down" in red for a Givmo takedown, read off each row's
// `lifecycle.removed_by_givmo` (the list carries the hints; an older backend's rows read Deactivated). "Create fundraiser" opens
// the wizard; a row opens the manage page. Permission is Member-level (`company:manage_fundraisers`) —
// EVERY admin qualifies, so there is NO Owner-only gate here.
//
// DARK: the whole area is gated by `isCompanyFundraisersEnabled()` (VITE_COMPANY_FUNDRAISERS_ENABLED,
// default off). The `/company-fundraisers` route redirects when off; this page ALSO bounces (defense in
// depth) so it can never paint while dark.

import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { companiesListCompanyFundraisersOptions } from '@givmo/api-client/query';
import { isGivmoError } from '@givmo/api-client';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { HeartHandshake, Plus } from 'lucide-react';
import { isCompanyFundraisersEnabled } from '../../lib/env';
import {
  extractCompanyFundraisers,
  fundraiserDonationCountForDisplay,
  fundraiserGoal,
  fundraiserRaised,
  fundraiserStatusView,
} from './company-fundraisers-decode';

const PREFIX = 'company-fundraisers';

export function CompanyFundraisersPage() {
  const navigate = useNavigate();
  const enabled = isCompanyFundraisersEnabled();

  useEffect(() => {
    if (!enabled) void navigate({ to: '/dashboard' });
  }, [enabled, navigate]);
  if (!enabled) return null;

  return <CompanyFundraisersList />;
}

function CompanyFundraisersList() {
  const navigate = useNavigate();
  const list = useQuery(companiesListCompanyFundraisersOptions());
  const fundraisers = extractCompanyFundraisers(list.data);

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-company-fundraisers')}
    >
      <PageHeader
        title="Company Fundraisers"
        subtitle="Create and manage fundraisers your company runs on Givmo."
        actions={
          <Button
            appearance="saas"
            tone="primary"
            leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
            onClick={() => void navigate({ to: '/company-fundraisers/new' })}
            testId={`${PREFIX}-create`}
          >
            Create fundraiser
          </Button>
        }
      />

      {list.isPending ? (
        <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-loading`)}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[92px] w-full rounded-lg" />
          ))}
        </div>
      ) : list.isError ? (
        <Card {...testIdProp(`${PREFIX}-error`)}>
          <CardBody className="flex flex-col items-center gap-8 py-24 text-center">
            <p className="text-s14 font-w500 text-danger-700">
              {isGivmoError(list.error) ? list.error.message : 'Failed to load fundraisers.'}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void list.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : fundraisers.length === 0 ? (
        <EmptyState
          testId={`${PREFIX}-empty-state`}
          icon={<Icon icon={HeartHandshake} size={20} aria-hidden />}
          title="No fundraisers yet"
          description="Create your company's first fundraiser to start raising for the causes you care about."
        />
      ) : (
        <ul className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-list`)}>
          {fundraisers.map((f) => {
            const status = fundraiserStatusView(f);
            const goal = fundraiserGoal(f);
            const raised = fundraiserRaised(f);
            const donationCount = fundraiserDonationCountForDisplay(f);
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: '/company-fundraisers/$fundraiserId',
                      params: { fundraiserId: f.id },
                    })
                  }
                  className="flex w-full items-center gap-16 rounded-lg border border-border-subtle bg-surface p-16 text-left outline-none transition-colors hover:bg-surface-hover focus-visible:border-brand-500"
                  {...testIdProp(`${PREFIX}-row-${f.id}`)}
                >
                  {/* Cover thumbnail — the wire provides `image_url`; the Avatar renders initials otherwise. */}
                  <Avatar
                    src={f.image_url ?? undefined}
                    name={f.name}
                    size="lg"
                    className="rounded-lg"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <div className="flex items-center gap-8">
                      <span className="truncate font-w600 text-text-strong">{f.name}</span>
                      <Badge tone={status.tone} dot testId={`${PREFIX}-row-${f.id}-status`}>
                        {status.label}
                      </Badge>
                    </div>
                    <span className="text-s13 text-text-secondary tabular-nums">
                      {raised === null ? 'Raised unavailable' : `${raised} raised`}
                      {goal !== null ? ` · ${goal} goal` : ''}
                      {' · '}
                      {donationCount === null
                        ? 'Donations unavailable'
                        : `${donationCount} ${donationCount === 1 ? 'donation' : 'donations'}`}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
